/**
 * Infinite Loop Detection Error Boundary
 * Detects and prevents React error #185 (Maximum update depth exceeded)
 * Provides graceful recovery mechanisms for production resilience
 */

import React, { Component, ReactNode, ErrorInfo } from 'react';
import { InfiniteLoopRecovery } from './InfiniteLoopRecovery';

interface InfiniteLoopBoundaryProps {
  children: ReactNode;
  onLoopDetected?: (error: Error) => void;
  maxUpdatesPerInterval?: number;
  intervalMs?: number;
  fallbackComponent?: ReactNode;
}

interface InfiniteLoopBoundaryState {
  hasInfiniteLoop: boolean;
  hasError: boolean;
  error?: Error;
  errorInfo?: ErrorInfo;
  recoveryAttempts: number;
}

export class InfiniteLoopBoundary extends Component<
  InfiniteLoopBoundaryProps, 
  InfiniteLoopBoundaryState
> {
  private updateCount = 0;
  private lastUpdateTime = 0;
  private intervalMs: number;
  private maxUpdatesPerInterval: number;
  private recoveryTimer?: NodeJS.Timeout;
  private performanceMonitor?: PerformanceObserver;
  
  constructor(props: InfiniteLoopBoundaryProps) {
    super(props);
    
    this.state = {
      hasInfiniteLoop: false,
      hasError: false,
      recoveryAttempts: 0
    };
    
    this.intervalMs = props.intervalMs || 100; // 100ms window
    this.maxUpdatesPerInterval = props.maxUpdatesPerInterval || 50; // Max 50 updates per 100ms
    
    // Monitor performance for additional loop detection
    this.initializePerformanceMonitoring();
  }

  private initializePerformanceMonitoring(): void {
    if (typeof PerformanceObserver !== 'undefined') {
      try {
        this.performanceMonitor = new PerformanceObserver((list) => {
          const entries = list.getEntries();
          const longTasks = entries.filter(entry => entry.duration > 50); // Tasks > 50ms
          
          if (longTasks.length > 10) {
            console.warn('🔄 Performance: Detected multiple long tasks, possible infinite loop');
            this.handleInfiniteLoopDetection(new Error('Performance degradation detected'));
          }
        });
        
        this.performanceMonitor.observe({ entryTypes: ['measure', 'navigation'] });
      } catch (error) {
        console.warn('Performance monitoring not available:', error);
      }
    }
  }

  componentDidUpdate(): void {
    const now = Date.now();
    
    // Reset counter if enough time has passed
    if (now - this.lastUpdateTime > this.intervalMs) {
      this.updateCount = 0;
      this.lastUpdateTime = now;
      return;
    }
    
    // Increment update count within the time window
    this.updateCount++;
    
    // Check for infinite loop condition
    if (this.updateCount > this.maxUpdatesPerInterval) {
      const error = new Error(
        `Infinite loop detected: ${this.updateCount} updates in ${this.intervalMs}ms`
      );
      
      console.error('🚨 [InfiniteLoopBoundary] Loop detected:', {
        updateCount: this.updateCount,
        intervalMs: this.intervalMs,
        component: this.constructor.name,
        timestamp: new Date().toISOString()
      });
      
      this.handleInfiniteLoopDetection(error);
    }
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    // Check if this is the specific React error #185
    const isMaxUpdateDepthError = error.message.includes('Maximum update depth exceeded') ||
                                 error.message.includes('Too many re-renders') ||
                                 error.stack?.includes('updateDepth');
    
    if (isMaxUpdateDepthError) {
      console.error('🚨 [InfiniteLoopBoundary] React error #185 caught:', {
        error: error.message,
        errorInfo,
        recoveryAttempts: this.state.recoveryAttempts
      });
      
      this.handleInfiniteLoopDetection(error);
    } else {
      // Handle other errors normally
      this.setState({
        hasError: true,
        error,
        errorInfo
      });
    }
  }

  private handleInfiniteLoopDetection(error: Error): void {
    // Notify parent component
    this.props.onLoopDetected?.(error);
    
    // Set loop detected state
    this.setState(prevState => ({
      hasInfiniteLoop: true,
      error,
      recoveryAttempts: prevState.recoveryAttempts + 1
    }));
    
    // Auto-recovery mechanism after 3 seconds
    this.scheduleAutoRecovery();
  }

  private scheduleAutoRecovery(): void {
    if (this.recoveryTimer) {
      clearTimeout(this.recoveryTimer);
    }
    
    this.recoveryTimer = setTimeout(() => {
      if (this.state.recoveryAttempts < 3) {
        console.log('🔄 [InfiniteLoopBoundary] Attempting auto-recovery...');
        this.handleRecovery();
      } else {
        console.error('🚨 [InfiniteLoopBoundary] Max recovery attempts reached');
      }
    }, 3000);
  }

  private handleRecovery = (): void => {
    console.log('🔄 [InfiniteLoopBoundary] Manual recovery initiated');
    
    // Reset all counters and state
    this.updateCount = 0;
    this.lastUpdateTime = 0;
    
    this.setState({
      hasInfiniteLoop: false,
      hasError: false,
      error: undefined,
      errorInfo: undefined
    });
    
    // Clear any scheduled recovery
    if (this.recoveryTimer) {
      clearTimeout(this.recoveryTimer);
      this.recoveryTimer = undefined;
    }
  };

  private handleForceReload = (): void => {
    console.log('🔄 [InfiniteLoopBoundary] Force reload initiated');
    window.location.reload();
  };

  componentWillUnmount(): void {
    if (this.recoveryTimer) {
      clearTimeout(this.recoveryTimer);
    }
    
    if (this.performanceMonitor) {
      this.performanceMonitor.disconnect();
    }
  }

  render(): ReactNode {
    // Show infinite loop recovery UI
    if (this.state.hasInfiniteLoop) {
      if (this.props.fallbackComponent) {
        return this.props.fallbackComponent;
      }
      
      return (
        <InfiniteLoopRecovery
          error={this.state.error}
          recoveryAttempts={this.state.recoveryAttempts}
          onRecover={this.handleRecovery}
          onForceReload={this.handleForceReload}
        />
      );
    }
    
    // Show general error UI
    if (this.state.hasError) {
      return (
        <InfiniteLoopRecovery
          error={this.state.error}
          recoveryAttempts={this.state.recoveryAttempts}
          onRecover={this.handleRecovery}
          onForceReload={this.handleForceReload}
          isGeneralError={true}
        />
      );
    }
    
    return this.props.children;
  }
}

export default InfiniteLoopBoundary;
